<template>
    <modal
        id="finance-transaction-detail-modal"
        v-model="showModal"
        :title="$t('transaction')"
        :hide-footer="true"
        :scrollable="true"
    >
        <div v-if="loading" class="py-4 text-center">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </div>
        <div v-else-if="detail">
            <div class="row">
                <div class="col-md-6 mb-3">
                    <p class="text-muted mb-1">{{ $t('category') }}</p>
                    <p class="mb-0 font-weight-600">{{ $displayByLocale(detail.category.name) || '--' }}</p>
                </div>
                <div class="col-md-6 mb-3">
                    <p class="text-muted mb-1">{{ $t('type') }}</p>
                    <p class="mb-0 font-weight-600 badge badge-pill"
                        :class="typeLabel === 'income' ? 'badge-success' : 'badge-warning'">
                        {{ $t(typeLabel) }}
                    </p>
                </div>
                <div class="col-md-6 mb-3">
                    <p class="text-muted mb-1">{{ $t('account') }}</p>
                    <p class="mb-0 font-weight-600">{{ detail.source_account.name }}</p>
                </div>
                <div class="col-md-6 mb-3">
                    <p class="text-muted mb-1">{{ $t('amount') }}</p>
                    <p class="mb-0 font-weight-600">{{ formattedAmount }}</p>
                </div>
                <div class="col-md-6 mb-3">
                    <p class="text-muted mb-1">{{ $t('tax') }}</p>
                    <p class="mb-0 font-weight-600">{{ taxLabel }}</p>
                </div>
                <div class="col-md-6 mb-3">
                    <p class="text-muted mb-1">{{ $t('reference_code') }}</p>
                    <p class="mb-0 font-weight-600">{{ detail.reference_code || '--' }}</p>
                </div>
                <div class="col-md-6 mb-3">
                    <p class="text-muted mb-1">{{ $t('date') }}</p>
                    <p class="mb-0 font-weight-600">{{ formattedDate }}</p>
                </div>
                <div class="col-md-6 mb-3">
                    <p class="text-muted mb-1">{{ $t('created_by') }}</p>
                    <p class="mb-0 font-weight-600">{{ detail.created_by?.full_name || '--' }}</p>
                </div>
                <div class="col-md-12 mb-3">
                    <p class="text-muted mb-1">{{ $t('tag') }}</p>
                    <div v-if="tags.length" class="d-flex flex-wrap">
                        <span
                            v-for="tag in tags"
                            :key="tag.id"
                            class="badge badge-primary mr-2 mb-2"
                        >
                            {{ tag.title }}
                        </span>
                    </div>
                    <p v-else class="mb-0 font-weight-600">--</p>
                </div>
                <div class="col-md-12 mb-3">
                    <p class="text-muted mb-1">{{ $t('remark') }}</p>
                    <p class="mb-0 font-weight-600">{{ detail.remark || '--' }}</p>
                </div>
                <div class="col-md-12 mb-3" v-if="files.length">
                    <p class="text-muted mb-1">{{ $t('attachments') }}</p>
                    <ul class="mb-0 pl-3">
                        <li v-for="(file, index) in files" :key="index">
                            <a :href="file" target="_blank" rel="noopener noreferrer">
                                {{ extractFileName(file) }}
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div v-if="detail.confirmations.length" class="mt-3 pt-3 border-top">
                <h6 class="mb-3">{{ $t('confirm') }}</h6>
                <div class="row mb-4" v-for="confirmation in detail.confirmations" :key="confirmation.id">
                    <div class="col-md-6 mb-3">
                        <p class="text-muted mb-1">{{ $t('remark') }}</p>
                        <p class="mb-0 font-weight-600">{{ confirmation.remark || '--' }}</p>
                    </div>
                    <div class="col-md-3 mb-3">
                        <p class="text-muted mb-1">{{ $t('created_by') }}</p>
                        <p class="mb-0 font-weight-600">{{ confirmation.confirmer?.full_name || '--' }}</p>
                    </div>
                    <div class="col-md-3 mb-3">
                        <p class="text-muted mb-1">{{ $t('date') }}</p>
                        <p class="mb-0 font-weight-600">{{ confirmation.confirmed_at || '--' }}</p>
                    </div>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import {TENANT_BASE_URL} from "../../../../common/Config/UrlHelper";
import {axiosGet, urlGenerator} from "../../../../common/Helper/AxiosHelper";
import {formatCurrency, numberFormatter} from "../../../../common/Helper/Support/SettingsHelper";
import {formatDateToLocal} from "../../../../common/Helper/Support/DateTimeHelper";

export default {
    name: "TransactionDetailModal",
    mixins: [ModalMixin],
    props: {
        transactionId: {
            type: [Number, String],
            required: true,
        },
    },
    data() {
        return {
            detail: null,
            loading: false,
        }
    },
    computed: {
        formattedAmount() {
            const amount = Number(this.detail?.amount || 0);
            return amount ? formatCurrency(numberFormatter(amount)) : '--';
        },
        taxLabel() {
            const tax = this.detail?.tax;
            return tax ? formatCurrency(numberFormatter(Number(tax))) : '--';
        },
        formattedDate() {
            return this.detail?.process_date ? formatDateToLocal(this.detail.process_date) : '--';
        },
        confirmationDate() {
            return this.detail?.confirmation?.confirmed_at ? formatDateToLocal(this.detail.confirmation.confirmed_at) : '--';
        },
        typeLabel() {
            if (this.detail?.type === 'income') {
                return this.$t('income');
            }
            if (this.detail?.type === 'expense') {
                return this.$t('expense');
            }
            return '--';
        },
        tags() {
            return this.detail?.tags || [];
        },
        files() {
            const list = this.detail?.files || [];
            return list.map(file => urlGenerator(file));
        }
    },
    created() {
        this.showModal = this.value;
        if (this.showModal) {
            this.fetchDetail();
        }
    },
    methods: {
        fetchDetail() {
            this.loading = true;
            axiosGet(`${TENANT_BASE_URL}app/finance-transactions/${this.transactionId}`)
                .then(({data}) => {
                    this.detail = data;
                })
                .catch(() => {
                    this.$toastr.e(this.$t('something_went_wrong'));
                    this.showModal = false;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        extractFileName(path) {
            if (!path) {
                return '';
            }
            return path.split('/').pop();
        },
    },
}
</script>
